import cloneDeep from 'lodash/cloneDeep';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { getOrParseContentBodyAdf } from '@confluence/adf-analysis-core/entry-points/getOrParseContentBodyAdf.ts';
import { expValEquals } from '@confluence/feature-experiments';

/**
 * Page ADF resolution shared by the two Object Sidebar Remix entry points: the button's own
 * popup and the Rovo-sidebar submit coordinator hosted by the lifecycle session. Both start
 * from the same page, so they must not drift on how that page is read.
 */
export type ObjectSidebarRemixAdfSource = {
	contentId?: string;
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
};

/** Returns an owned document for Remix, or the shared document for read-only checks. */
export const parseObjectSidebarRemixAdf = (
	value: string,
	contentId?: string,
	{ readOnly = false }: { readOnly?: boolean } = {},
): DocNode | null => {
	try {
		if (contentId && expValEquals('cc-proj-xl-use-shared-adf-parsing', 'isEnabled', true)) {
			const result = getOrParseContentBodyAdf(contentId, value);
			if (!result.ok || !result.doc || result.doc.type !== 'doc') {
				return null;
			}
			// Remix's fragment filtering can mutate nested content; only checks share the cache object.
			return readOnly ? result.doc : cloneDeep(result.doc);
		}
		const parsed = JSON.parse(value);
		if (!parsed || parsed.type !== 'doc') {
			return null;
		}
		return parsed as DocNode;
	} catch {
		return null;
	}
};

/** Resolves live editor ADF when available, otherwise the saved renderer content body. */
export const loadObjectSidebarRemixAdf = async ({
	contentId,
	contentBodyValue,
	getAdfDocument,
}: ObjectSidebarRemixAdfSource): Promise<DocNode | null> => {
	if (getAdfDocument) {
		return getAdfDocument();
	}
	if (!contentBodyValue) {
		return null;
	}
	return parseObjectSidebarRemixAdf(contentBodyValue, contentId);
};
